import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ExternalLink, Building2, Loader2, Search, CheckCircle2, X, Briefcase, HardHat, Plus, AlertTriangle, Phone, UserRound, UserPlus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AddCallToCrmModal } from './AddCallToCrmModal';
import { StepModePicker } from './AddToCrm/StepModePicker';
import { StepAccountQuote } from './AddToCrm/StepAccountQuote';
import { StepProject } from './AddToCrm/StepProject';
import { StepPackage } from './AddToCrm/StepPackage';
import { PRODUCT_COLOURS, productColour, ColourDot, INPUT_CLS, normalizeSearchQuery } from './AddToCrm/constants';
import RecentQuotesPicker from './RecentQuotesPicker';
import { ContactNotesTasksModal } from './ContactNotesTasksModal';
import QuoteVersionHistoryModal from './QuoteVersionHistoryModal';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddToCrmModal = ({ isOpen, onClose, quoteForm, pdfFile, subtotal, fastTrack = false, onChainToCall = null, initialMode = null, initialAccount = null, initialProject = null, zIndex = 80, onCrmSuccess = null, resumeSnapshot = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  // step 0 = mode picker, then mode-specific steps:
  //   mode='quote'   → 1 = Account, 2 = Quote Details
  //   mode='project' → 1 = Project, 2 = Contractor & Package, 3 = Quote Details
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null); // 'project' | 'quote' | null
  const isProject = mode === 'project';
  const [customerSearchName, setCustomerSearchName] = useState(null);
  const [historyItem, setHistoryItem] = useState(null); // for QuoteVersionHistoryModal

  // Fast-track flow (Add Quote / Project pill) — the user opens the modal
  // before picking a PDF. The modal itself collects the PDF after step 0
  // (quote mode) or after step 2 (project mode, only if user opts into a
  // quote). Once parsed, `localPdfFile` overrides the incoming prop.
  const [localPdfFile, setLocalPdfFile] = useState(null);
  const [localQuoteForm, setLocalQuoteForm] = useState(null);
  const [localSubtotal, setLocalSubtotal] = useState(0);
  // grandTotal — the quote value shown to and editable by the user.
  // For fast-track it's set from the parsed PDF total; for other paths it is
  // pre-filled from the `subtotal` prop (ex-VAT line items sum).
  const [grandTotal, setGrandTotal] = useState(
    subtotal ? Number(subtotal).toFixed(2) : ''
  );
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfDragOver, setPdfDragOver] = useState(false);
  // Project fast-track: after package is chosen, ask the user "has this been
  // quoted?". 'asking' | 'yes' (PDF upload visible) | 'no' (skip to submit).
  const [projectQuotedChoice, setProjectQuotedChoice] = useState(null);
  const fastTrackInputRef = useRef(null);

  // Parse a PDF the user dropped/picked inside the modal during fast-track.
  // Silent extraction — we don't push them through the Quote Editor; we just
  // capture customer_name + items + ex-VAT subtotal so the rest of the flow
  // (account suggestion / supplier auto-suggest) has data to work with.
  const parseFastTrackPdf = async (file) => {
    if (!file) return;
    setPdfParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`${API}/quotes/extract-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (!r.data?.success || !r.data?.data) {
        toast.error(r.data?.error || 'Could not parse PDF — try the Quotes page instead.');
        return;
      }
      const extracted = r.data.data;
      const items = Array.isArray(extracted.items) ? extracted.items : [];
      const sub = items.reduce((acc, it) => {
        const q = parseFloat(it.quantity) || 0;
        const up = parseFloat(it.unit_price) || 0;
        const disc = parseFloat(it.discount_pct) || 0;
        const line = up * q * (1 - (disc / 100));
        return acc + (Number.isFinite(line) ? line : 0);
      }, 0);
      setLocalPdfFile(file);
      setLocalQuoteForm({
        // No `id` — fast-track quotes are not saved as C30 quotes.
        customer_name: extracted.customer_name || '',
        customer_address: extracted.customer_address || '',
        quote_number: extracted.quote_number || '',
        project_ref: extracted.project_ref || '',
        reference: extracted.reference || '',
        date: extracted.date || '',
        valid_until: extracted.valid_until || '',
        notes: extracted.notes || '',
        items,
      });
      setLocalSubtotal(Number(sub.toFixed(2)));

      // Use normalised customer name so searches like "B & Q(HALESOWEN)(200775)"
      // resolve correctly to "B&Q Halesowen" in Zoho.
      if (extracted.customer_name) {
        setAccountQuery(normalizeSearchQuery(extracted.customer_name));
      }

      // Set grand total — prefer the PDF-parsed total (inc. VAT) from the LLM;
      // fall back to the ex-VAT line-items sum so the field is never blank.
      const parsedTotal = parseFloat(extracted.total || extracted.subtotal || 0);
      setGrandTotal((parsedTotal > 0 ? parsedTotal : sub).toFixed(2));

      // Build initial subject: "<quote_number> - <reference>"
      // The supplier name is prepended asynchronously once fetchSupplierSuggestion resolves.
      const qNum = extracted.quote_number || '';
      const ref = extracted.reference || extracted.project_ref || '';
      if (qNum || ref) {
        const base = [qNum, ref].filter(Boolean).join(' - ');
        setSubject(base);
        setSubjectAutoGenerated(true);
      }
      toast.success('PDF parsed — customer & items captured silently.');
    } catch (e) {
      toast.error('Failed to parse PDF — please try again.');
    } finally {
      setPdfParsing(false);
    }
  };

  const handleFastTrackDrop = (e) => {
    e.preventDefault();
    setPdfDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) parseFastTrackPdf(file);
  };
  const handleFastTrackBrowse = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) parseFastTrackPdf(file);
  };

  // Select a recent quote from the embedded picker — bypasses PDF upload entirely.
  const handleSelectRecentQuote = (selectedForm) => {
    setLocalQuoteForm(selectedForm);
    setLocalPdfFile(null);
    // Populate the customer search bar from the selected quote — mirrors the
    // PDF-upload path (parseFastTrackPdf). Without this, picking an emailed/parsed
    // quote filled the line items but left the account search empty.
    const custName = selectedForm?.customer_name || '';
    if (custName) {
      setSelectedAccount(null);
      setAccountQuery(normalizeSearchQuery(custName));
    }
    // Quote Value + subtotal — also mirror the PDF path so the quote-details step
    // is pre-filled identically regardless of how the quote was added.
    const items = Array.isArray(selectedForm?.items) ? selectedForm.items : [];
    const sub = items.reduce((acc, it) => {
      const q = parseFloat(it.quantity) || 0;
      const up = parseFloat(it.unit_price) || 0;
      const disc = parseFloat(it.discount_pct) || 0;
      const line = up * q * (1 - (disc / 100));
      return acc + (Number.isFinite(line) ? line : 0);
    }, 0);
    setLocalSubtotal(Number(sub.toFixed(2)));
    const parsedTotal = parseFloat(selectedForm?.total) || 0;
    setGrandTotal((parsedTotal > 0 ? parsedTotal : sub).toFixed(2));
    const qNum = selectedForm?.quote_number || '';
    const ref = selectedForm?.reference || selectedForm?.project_ref || '';
    if (qNum || ref) {
      setSubject([qNum, ref].filter(Boolean).join(' - '));
      setSubjectAutoGenerated(true);
    }
    // Supplier suggestion, specialism, product & contact effects fire via the
    // localQuoteForm / selectedAccount changes above.
  };

  // Effective form/file/subtotal — local overrides prop in fast-track mode.
  const effectiveForm = (fastTrack && localQuoteForm) ? localQuoteForm : quoteForm;
  const effectivePdf = (fastTrack && localPdfFile) ? localPdfFile : pdfFile;
  const effectiveSubtotal = (fastTrack && localQuoteForm) ? localSubtotal : subtotal;

  // ─── Account state (used directly in quote mode; in project mode the account
  // is derived from the chosen contractor package) ──────────────────────
  const [accountQuery, setAccountQuery] = useState(normalizeSearchQuery(quoteForm?.customer_name || ''));
  const [accountLoading, setAccountLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  // Contacts for the selected account — auto-loaded when account resolves
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  // Inline "Add new contact" mini-form on the quote details step
  const [showQuoteAddContact, setShowQuoteAddContact] = useState(false);
  const [addingQuoteContact, setAddingQuoteContact] = useState(false);
  const [qNewFirst, setQNewFirst] = useState('');
  const [qNewLast, setQNewLast] = useState('');
  const [qNewPhone, setQNewPhone] = useState('');
  const [qNewEmail, setQNewEmail] = useState('');
  const [qNewTitle, setQNewTitle] = useState('');
  // Inline "Add new prospect customer" flow — shown below the account search.
  const [showProspectCreate, setShowProspectCreate] = useState(false);
  const [prospectAccountName, setProspectAccountName] = useState('');
  const [prospectAccountPhone, setProspectAccountPhone] = useState('');
  const [prospectContactFirst, setProspectContactFirst] = useState('');
  const [prospectContactLast, setProspectContactLast] = useState('');
  const [prospectContactEmail, setProspectContactEmail] = useState('');
  const [prospectContactMobile, setProspectContactMobile] = useState('');
  const [creatingProspect, setCreatingProspect] = useState(false);

  // ─── Project state ─────────────────────────────────────────────────────
  const [projectQuery, setProjectQuery] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectCreate, setShowProjectCreate] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectBarbourId, setNewProjectBarbourId] = useState('');
  const [newProjectPostcode, setNewProjectPostcode] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  // ─── "Search Barbour" (project step) — find a Barbour ABI project by
  // postcode, then "Check CRM" per result (Barbour ID → postcode → name),
  // then either select the existing CRM match or jump into "Create new
  // project" pre-filled with the Barbour result ─────────────────────────
  const [showBarbourSearch, setShowBarbourSearch] = useState(false);
  const [barbourPostcode, setBarbourPostcode] = useState('');
  const [barbourRadius, setBarbourRadius] = useState('0.1');
  const [barbourMaxRadius, setBarbourMaxRadius] = useState(5);
  const [barbourSearching, setBarbourSearching] = useState(false);
  const [barbourResults, setBarbourResults] = useState(null);
  const [barbourSearchError, setBarbourSearchError] = useState('');
  // Keyed by barbour_id (falls back to project_name) → { checking, found, error }
  const [crmCheckStatus, setCrmCheckStatus] = useState({});

  // Prefill the radius field with the standard "Barbour Search V2" default
  // and fetch the super-admin-configured max radius (used as the input's
  // cap) fresh every time this panel opens.
  useEffect(() => {
    if (!showBarbourSearch) return;
    (async () => {
      try {
        const [defRes, maxRes] = await Promise.all([
          api.get(`${API}/barbour/v2-default-radius`),
          api.get(`${API}/barbour/max-radius`),
        ]);
        setBarbourRadius(String(defRes.data?.radius_miles ?? '0.1'));
        setBarbourMaxRadius(maxRes.data?.max_radius_miles ?? 5);
      } catch (e) {
        // non-fatal — search still works using the backend's own default
      }
    })();
  }, [showBarbourSearch]);

  // ─── Step 3: contractor / package ──────────────────────────────────────
  const [contractorRole, setContractorRole] = useState('main'); // 'main' | 'sub'
  // Tracks whether the user has explicitly clicked a tab. If true, auto-switch is disabled.
  const userPickedRoleRef = useRef(false);
  const setContractorRoleByUser = (role) => { userPickedRoleRef.current = true; setContractorRole(role); };
  const [projectPackages, setProjectPackages] = useState({ contractor_packages: [], sub_contractor_packages: [] });
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedContractorPackageId, setSelectedContractorPackageId] = useState('');
  const [selectedSubContractorPackageId, setSelectedSubContractorPackageId] = useState('');
  // Two-stage contractor picker: first pick a contractor, then a package under them.
  const [selectedContractorAccountId, setSelectedContractorAccountId] = useState('');
  const [packageTypes, setPackageTypes] = useState({ contractor_package_types: [], sub_contractor_package_types: [] });
  const [packageTypesLoading, setPackageTypesLoading] = useState(false);
  // Create-new-package state
  const [showNewPackage, setShowNewPackage] = useState(false);
  const [newContractorAccountQuery, setNewContractorAccountQuery] = useState('');
  const [newContractorAccounts, setNewContractorAccounts] = useState([]);
  const [newContractorAccountLoading, setNewContractorAccountLoading] = useState(false);
  const [newContractorAccount, setNewContractorAccount] = useState(null);
  const [newPackageType, setNewPackageType] = useState('');
  const [newPackageParentCP, setNewPackageParentCP] = useState(''); // optional parent for sub package
  const [newPackageCategories, setNewPackageCategories] = useState([]); // multi-picklist (sub only)
  const [newPackageEnconStartDate, setNewPackageEnconStartDate] = useState(''); // Encon start date
  const [creatingPackage, setCreatingPackage] = useState(false);

  // ─── Step 4: quote details ─────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productId, setProductId] = useState('');
  const [businessSpecialism, setBusinessSpecialism] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierError, setSupplierError] = useState(null);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [supplierQuery, setSupplierQuery] = useState('');
  // Auto-suggested supplier — derived from the most-used product code prefix
  // on the quote, looked up against the C30 stock database (manual_supplier_groups).
  // The user can accept (one click) or ignore and type their own.
  const [supplierSuggestion, setSupplierSuggestion] = useState(null); // { name, raw_name, supplier_group_id, matched_prefix, matched_code_count, total_codes, pag_name, user_confirmed }
  const [supplierSuggestionLoading, setSupplierSuggestionLoading] = useState(false);
  const [supplierSuggestionDismissed, setSupplierSuggestionDismissed] = useState(false);
  // True when the suggestion was auto-applied — lets the auto-select pick the
  // first Zoho result even if it's not an exact string match.
  const [supplierAutoApplied, setSupplierAutoApplied] = useState(false);
  // Smart-search state: when the suggestion is applied we first try the
  // full name in Zoho; if that returns 0 hits we automatically refine to
  // just the first word (e.g. "KNAUF DRYWALL" → "KNAUF"). If even the
  // refined search returns 0 we surface a hint so the rep knows to type
  // their own search.
  const [supplierSearchRefined, setSupplierSearchRefined] = useState(false);
  const [supplierSearchExhausted, setSupplierSearchExhausted] = useState(false);
  const [contribution, setContribution] = useState('');
  // Subject must NEVER be seeded from notes — notes are free-text and unrelated
  // to the CRM quote subject. Subject is always: QuoteNumber - Reference (supplier
  // gets prepended once the auto-suggestion resolves).
  const [subject, setSubject] = useState(
    [quoteForm?.quote_number, quoteForm?.reference].filter(Boolean).join(' - ')
  );
  // Tracks whether subject was auto-generated from quote data (so we can
  // append the supplier name once it resolves without overriding manual edits).
  const [subjectAutoGenerated, setSubjectAutoGenerated] = useState(false);
  const [followUpDays, setFollowUpDays] = useState(3);
  const [packageClosingDate, setPackageClosingDate] = useState('');
  const [quoteStage, setQuoteStage] = useState('Negotiation Tender');

  // ─── Submit / result ───────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  // Drives the embedded AddCallToCrmModal opened from the success panel —
  // lets the user log a call against the same account without re-picking it.
  const [showAddCallModal, setShowAddCallModal] = useState(false);

  // ─── Request-access (NO_ZOHO_USER) ─────────────────────────────────────
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [accessRequestReason, setAccessRequestReason] = useState('');
  const [accessRequestEmail, setAccessRequestEmail] = useState('');
  const [accessRequestSent, setAccessRequestSent] = useState(false);
  const [accessRequestSending, setAccessRequestSending] = useState(false);

  const todayISO = new Date().toISOString().slice(0, 10);

  // Format ISO date (YYYY-MM-DD) to dd/mm/yyyy display
  const formatDate = (iso) => {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return iso;
  };

  // Generate / download PDF for the submitted quote
  const handleGeneratePdf = async () => {
    // If an original uploaded PDF is available, download that directly
    if (effectivePdf) {
      const url = URL.createObjectURL(effectivePdf);
      const a = document.createElement('a');
      a.href = url;
      a.download = effectivePdf.name || `Quote_${effectiveForm?.quote_number || 'download'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    }
    // Otherwise generate a fresh PDF from form data
    if (!effectiveForm) return;
    setPdfGenerating(true);
    try {
      const items = Array.isArray(effectiveForm.items) ? effectiveForm.items : [];
      const sub = items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0), 0);
      const vat = sub * 0.2;
      const res = await api.post(`${API}/quotes/generate-pdf-only`, {
        ...effectiveForm,
        subtotal: sub.toFixed(2),
        vat: vat.toFixed(2),
        total: (sub + vat).toFixed(2),
      }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerSlug = (effectiveForm.customer_name || 'Quote').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `Quote_${effectiveForm.quote_number || 'Q'}_${customerSlug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // ─── Effects ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    loadProducts();
    loadSuppliers('');
    loadPackageTypes();
    fetchSupplierSuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // When the supplier suggestion resolves, decide whether to auto-apply or
  // just surface a clickable chip:
  //   AUTO-APPLY when:
  //     - A human previously confirmed this supplier mapping (user_confirmed=true), OR
  //     - ≥70% of the quote's codes match this supplier AND the supplier name
  //       appears somewhere in the quote item descriptions or codes (strong signal)
  //   SHOW CHIP (user must click to accept) for everything else.
  //   This prevents Danosa (or any other default) from auto-filling when the
  //   system isn't confident enough.
  useEffect(() => {
    if (!supplierSuggestion?.name) return;

    const totalCodes = supplierSuggestion.total_codes || 1;
    const confidence = supplierSuggestion.matched_code_count / totalCodes;

    // Check if supplier name appears in item descriptions or codes
    const needleWords = supplierSuggestion.name.toLowerCase().split(/\s+/);
    const quoteText = [
      effectiveForm?.customer_name || '',
      ...(effectiveForm?.items || []).map(i => `${i.description || ''} ${i.code || ''}`),
    ].join(' ').toLowerCase();
    const supplierInQuote = needleWords.some(w => w.length > 3 && quoteText.includes(w));

    const isHighConfidence =
      supplierSuggestion.user_confirmed ||          // human-confirmed mapping
      (confidence >= 0.7 && supplierInQuote);       // strong code+text match

    if (!supplierId && !supplierSuggestionDismissed) {
      if (isHighConfidence) {
        // Auto-apply: pre-fill the supplier search so Zoho lookup fires
        applySupplierSuggestion();
      }
      // else: leave the supplier field blank — the chip below lets user click to apply
    }

    // Update subject format: Supplier - OrderNumber - Reference (only for auto-applied)
    if (isHighConfidence && subjectAutoGenerated) {
      const form = effectiveForm;
      const qNum = form?.quote_number || '';
      const ref = form?.reference || form?.project_ref || '';
      const supplier = supplierSuggestion.name;
      const parts = [supplier, qNum, ref].filter(Boolean);
      if (parts.length > 0) {
        setSubject(parts.join(' - '));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierSuggestion]);

  // Auto-set business specialism from supplier's PAG category
  // structural → "Encon Structural", industrial → "Encon Technical", else → "Encon Insulation"
  useEffect(() => {
    if (!supplierSuggestion) return;
    if (businessSpecialism) return; // Don't override manual pick
    const specialisms = packageTypes.quote_business_specialisms || [];
    if (!specialisms.length) return;
    const findSpec = (needle) => specialisms.find(s => (s || '').toLowerCase().includes(needle)) || null;
    const pagCategory = supplierSuggestion.pag_category || 'structural';
    let chosenSpec = null;
    if (pagCategory === 'structural') {
      chosenSpec = findSpec('encon structural') || findSpec('structural');
    } else if (pagCategory === 'industrial') {
      chosenSpec = findSpec('encon technical') || findSpec('technical');
    }
    if (!chosenSpec) chosenSpec = findSpec('encon insulation') || findSpec('insulation');
    if (chosenSpec) setBusinessSpecialism(chosenSpec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierSuggestion, packageTypes]);

  // Auto-set default product to "Insulation" when products load (if not already set)
  useEffect(() => {
    if (!products.length || productId) return;
    const insulationProduct = products.find(p => (p.name || '').toLowerCase() === 'insulation');
    if (insulationProduct) setProductId(insulationProduct.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // On open: rebuild subject from quote_number + reference and mark as
  // auto-generated so the supplier name will be prepended when suggestion resolves.
  // Notes are NEVER included in the subject — they belong in the body/description.
  useEffect(() => {
    if (!isOpen) return;
    const qNum = quoteForm?.quote_number || '';
    const ref = quoteForm?.reference || '';
    if (qNum || ref) {
      const base = [qNum, ref].filter(Boolean).join(' - ');
      if (base) {
        setSubject(base);
        setSubjectAutoGenerated(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // only on open

  // initialMode pre-seed — when opened from Customer Management modal with a
  // pre-selected account + desired mode (quote/project). Skips step 0 entirely.
  useEffect(() => {
    if (!isOpen || !fastTrack || !initialMode || initialProject?.id) return;
    setMode(initialMode);
    if (initialAccount?.id) {
      setSelectedAccount({ id: initialAccount.id, name: initialAccount.name || '' });
      setAccountQuery(normalizeSearchQuery(initialAccount.name || ''));
      // quote: jump straight to details; project: user still searches for the project
      setStep(initialMode === 'quote' ? 2 : 1);
    } else {
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // intentionally runs only on open

  // initialProject pre-seed — when opened from ProjectDetailPanel with a known project.
  // Sets mode=project, pre-selects the project, and jumps straight to step 2
  // (contractor & package selection) — skipping the project search entirely.
  useEffect(() => {
    if (!isOpen || !fastTrack || !initialProject?.id) return;
    setMode('project');
    const proj = {
      id: initialProject.id,
      deal_name: initialProject.deal_name || initialProject.name || '',
      barbour_project_id: initialProject.barbour_project_id || '',
      barbour_start_date: initialProject.barbour_start_date || '',
      postcode: initialProject.postcode || '',
      stage: initialProject.stage || '',
    };
    setSelectedProject(proj);
    setNewPackageEnconStartDate(proj.barbour_start_date || '');
    setProjectQuery(initialProject.deal_name || initialProject.name || '');
    setStep(2);
    loadProjectPackages(initialProject.id);
    loadPackageTypes();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // intentionally runs only on open

  // Modal UI v2 — resume from a minimized "Open Activities" tab (ActivityModalHost
  // reopens this modal with only isOpen/onClose/resumeSnapshot; every other prop is
  // absent, so this restores the essentials: mode, step, and whichever account/
  // project/package was chosen. Deeper sub-form fields (contacts, new-package
  // drafts, etc.) intentionally reset — this gets the user back to the right
  // place, not a byte-for-byte replay.
  useEffect(() => {
    if (!isOpen || !resumeSnapshot) return;
    if (resumeSnapshot.mode) setMode(resumeSnapshot.mode);
    if (resumeSnapshot.step != null) setStep(resumeSnapshot.step);
    if (resumeSnapshot.selectedAccount) {
      setSelectedAccount(resumeSnapshot.selectedAccount);
      setAccountQuery(normalizeSearchQuery(resumeSnapshot.selectedAccount.name || ''));
    }
    if (resumeSnapshot.selectedProject) {
      setSelectedProject(resumeSnapshot.selectedProject);
      setProjectQuery(resumeSnapshot.selectedProject.deal_name || '');
      loadProjectPackages(resumeSnapshot.selectedProject.id);
      loadPackageTypes();
      loadProducts();
    }
    if (resumeSnapshot.selectedContractorPackageId) setSelectedContractorPackageId(resumeSnapshot.selectedContractorPackageId);
    if (resumeSnapshot.selectedSubContractorPackageId) setSelectedSubContractorPackageId(resumeSnapshot.selectedSubContractorPackageId);
    if (resumeSnapshot.localQuoteForm) setLocalQuoteForm(resumeSnapshot.localQuoteForm);
    if (resumeSnapshot.localSubtotal) setLocalSubtotal(resumeSnapshot.localSubtotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resumeSnapshot]);

  // Register this as the live Activity Bar entry the moment a mode is picked
  // (leaving step 0), and log it as a Recent view too — mirrors Customer
  // Management's handleSelectAccount. Fires once per fresh open.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !mode || activityIdRef.current) return;
    activityIdRef.current = `add-to-crm-${Date.now()}`;
    const title = mode === 'project' ? 'New Project Quote' : 'New Quote';
    activityBar.openActivity({ type: 'add_to_crm', activityId: activityIdRef.current, title, subtitle: 'Add to CRM', snapshot: { activityId: activityIdRef.current, mode, step } });
    activityBar.logRecentView({ type: 'add_to_crm', activityId: activityIdRef.current, title, subtitle: 'Add to CRM', snapshot: { activityId: activityIdRef.current, mode, step } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, mode]);

  // Keep the Activity Bar's live snapshot fresh as the user progresses.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !activityIdRef.current) return;
    const title = selectedProject?.deal_name || selectedAccount?.name || (mode === 'project' ? 'New Project Quote' : 'New Quote');
    activityBar.updateActiveSnapshot({
      activityId: activityIdRef.current, mode, step, selectedAccount, selectedProject,
      selectedContractorPackageId, selectedSubContractorPackageId,
      localQuoteForm, localSubtotal,
    }, { title, subtitle: 'Add to CRM' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, mode, step, selectedAccount, selectedProject, selectedContractorPackageId, selectedSubContractorPackageId, localQuoteForm, localSubtotal]);

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);

  // Chain pre-fill — when this modal is launched right after the call modal
  // ("Add & Then Quote"), the parent passes the resolved Zoho account id via
  // quoteForm.zoho_account_id. Auto-select the account so the user lands on
  // step 2 with no extra clicks.
  useEffect(() => {
    if (!isOpen || !fastTrack) return;
    const id = quoteForm?.zoho_account_id;
    if (id && !selectedAccount?.id) {
      setSelectedAccount({ id, name: quoteForm?.customer_name || '', zoho_account_number: quoteForm?.customer_account_number || '' });
      setAccountQuery(normalizeSearchQuery(quoteForm?.customer_name || ''));
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fastTrack]);

  // Reuse an already-confirmed CRM match — if the customer was already
  // matched to a specific Zoho Account while the quote itself was being
  // created (e.g. picked from the CRM search dropdown in Create Quote), that
  // same account is carried on quoteForm.zoho_account_id. Once the rep picks
  // "Standalone Quote" from the mode picker, skip the account search step
  // entirely instead of making them find/select the same customer again.
  useEffect(() => {
    if (!isOpen || mode !== 'quote' || selectedAccount?.id) return;
    const id = effectiveForm?.zoho_account_id;
    if (id) {
      setSelectedAccount({ id, name: effectiveForm?.customer_name || '', zoho_account_number: effectiveForm?.customer_account_number || '' });
      setAccountQuery(normalizeSearchQuery(effectiveForm?.customer_name || ''));
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  // Same idea for Project mode: the confirmed customer is usually the
  // contractor being quoted, so pre-fill the "new package" contractor-account
  // picker with it too — one less search for a match we already have.
  useEffect(() => {
    if (!showNewPackage || newContractorAccount?.id) return;
    const id = effectiveForm?.zoho_account_id;
    if (id) {
      setNewContractorAccount({ id, name: effectiveForm?.customer_name || '' });
      setNewContractorAccountQuery(effectiveForm?.customer_name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewPackage]);

  // Account search debounced
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => runAccountSearch(accountQuery), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountQuery, isOpen]);

  // Supplier search debounced
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => loadSuppliers(supplierQuery), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierQuery, isOpen]);

  // Project search debounced
  useEffect(() => {
    if (!isOpen || !isProject) return;
    const t = setTimeout(() => runProjectSearch(projectQuery), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQuery, isProject, isOpen]);

  // When a project is picked → fetch its packages and reset the user-role-pick flag
  useEffect(() => {
    if (!selectedProject?.id) return;
    userPickedRoleRef.current = false; // new project → allow auto-switch again
    setContractorRole('main');
    loadProjectPackages(selectedProject.id);
    setSelectedContractorPackageId('');
    setSelectedSubContractorPackageId('');
    setSelectedContractorAccountId('');
    // Default Encon Start Date to the project's Barbour Start Date
    setNewPackageEnconStartDate(selectedProject.barbour_start_date || '');
  }, [selectedProject]);

  // Auto-switch the contractor-role tab to whichever has packages, so the user lands
  // on the tab with content rather than an empty one.
  // IMPORTANT: Only fires when the user hasn't explicitly picked a tab themselves.
  useEffect(() => {
    if (!isProject || !selectedProject) return;
    if (userPickedRoleRef.current) return; // user has explicitly chosen — never override
    const cpCount = projectPackages.contractor_packages.length;
    const spCount = projectPackages.sub_contractor_packages.length;
    if (cpCount === 0 && spCount > 0 && contractorRole === 'main') setContractorRole('sub');
    if (spCount === 0 && cpCount > 0 && contractorRole === 'sub') setContractorRole('main');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPackages]);

  // Distinct contractor counts — Zoho returns one row per *package*, but the
  // tab badges should reflect how many unique contractor *accounts* exist
  // on the project (the rep cares about the people, not the package count).
  const mainContractorCount = useMemo(() => {
    const ids = new Set();
    (projectPackages.contractor_packages || []).forEach(p => {
      const k = p.contractor_account_id || (p.contractor_account_name || '').trim().toLowerCase();
      if (k) ids.add(k);
    });
    return ids.size;
  }, [projectPackages]);

  const subContractorCount = useMemo(() => {
    const ids = new Set();
    (projectPackages.sub_contractor_packages || []).forEach(p => {
      const k = p.sub_contractor_account_id || (p.sub_contractor_account_name || '').trim().toLowerCase();
      if (k) ids.add(k);
    });
    return ids.size;
  }, [projectPackages]);

  // Contractor account search debounced (for new-package flow)
  useEffect(() => {
    if (!showNewPackage) return;
    const t = setTimeout(() => runContractorAccountSearch(newContractorAccountQuery), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newContractorAccountQuery, showNewPackage]);

  // ---------------------------------------------------------------------
  // Re-run supplier suggestion once a fast-track parse populates the local
  // quote form (the initial call on mount finds no items because fast-track
  // opens with quoteForm=null).
  useEffect(() => {
    if (fastTrack && localQuoteForm) {
      fetchSupplierSuggestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuoteForm]);

  // Auto-fill Product + Business Specialism from the selected package's
  // type/category text. Reduces typing when the package already implies
  // what the quote is about. Runs when:
  //   • a package is selected, AND
  //   • the rep hasn't manually picked a product/specialism yet.
  // The defaults are only applied to *empty* fields — manual edits are
  // never overwritten when the rep navigates back.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'project') return;
    if (!products?.length) return;

    // Pull the chosen package + extract every keyword-bearing string we
    // can find on it.
    let pkg = null;
    if (contractorRole === 'main' && selectedContractorPackageId) {
      pkg = projectPackages.contractor_packages.find(p => p.id === selectedContractorPackageId);
    } else if (contractorRole === 'sub' && selectedSubContractorPackageId) {
      pkg = projectPackages.sub_contractor_packages.find(p => p.id === selectedSubContractorPackageId);
    }
    if (!pkg) return;

    const haystack = [
      pkg.package_type,
      pkg.name,
      ...(Array.isArray(pkg.package_category) ? pkg.package_category : []),
    ].filter(Boolean).join(' ').toLowerCase();
    if (!haystack.trim()) return;

    // 1) Product match — score each product by the longest substring of
    // its name that appears in the haystack. Word-boundary aware so
    // "Insulation" doesn't match a product called "Foo".
    const lowerHay = haystack;
    const scoreProduct = (name) => {
      const tokens = (name || '').toLowerCase().split(/[^a-z0-9]+/i).filter(t => t.length >= 3);
      let score = 0;
      tokens.forEach(t => { if (lowerHay.includes(t)) score += t.length; });
      return score;
    };
    // Direct keyword shortcuts override product scoring (e.g. "fire" → "Passive Fire Protection").
    const directProductHints = [
      { needle: 'passive fire', preferName: 'Passive Fire Protection' },
      { needle: 'fire',          preferName: 'Passive Fire Protection' },
      { needle: 'partition',     preferName: 'Partitioning' },
      { needle: 'drywall',       preferName: 'Drywall' },
      { needle: 'ceiling',       preferName: 'Ceilings' },
      { needle: 'technical',     preferName: 'Technical Insulation' },
      { needle: 'insulation',    preferName: 'Insulation' },
      { needle: 'construction',  preferName: 'Construction Products' },
      { needle: 'cp -',          preferName: 'Construction Products' }, // CP - Geotechnical etc.
    ];
    let chosenProduct = null;
    for (const hint of directProductHints) {
      if (lowerHay.includes(hint.needle)) {
        chosenProduct = products.find(p => (p.name || '').toLowerCase() === hint.preferName.toLowerCase());
        if (chosenProduct) break;
      }
    }
    if (!chosenProduct) {
      const ranked = products
        .map(p => ({ p, s: scoreProduct(p.name) }))
        .filter(x => x.s > 0)
        .sort((a, b) => b.s - a.s);
      chosenProduct = ranked[0]?.p || null;
    }
    if (chosenProduct && !productId) setProductId(chosenProduct.id);

    // 2) Business Specialism — try keyword match against the picklist.
    // Fallback per user request: leave blank when nothing clear matches
    // (so the default Encon Insulation can take over via Zoho's own
    // default). If the picklist contains "Encon Insulation" we use that
    // explicitly as the safety net.
    const specialisms = packageTypes.quote_business_specialisms || [];
    const findSpecialism = (needles) => {
      for (const n of needles) {
        const m = specialisms.find(s => (s || '').toLowerCase().includes(n));
        if (m) return m;
      }
      return null;
    };
    let chosenSpec = null;
    if (lowerHay.includes('fire')) chosenSpec = findSpecialism(['fire']);
    else if (lowerHay.includes('drywall')) chosenSpec = findSpecialism(['drywall']);
    else if (lowerHay.includes('ceiling')) chosenSpec = findSpecialism(['ceiling']);
    else if (lowerHay.includes('partition')) chosenSpec = findSpecialism(['partition', 'drywall']);
    else if (lowerHay.includes('insulation')) chosenSpec = findSpecialism(['encon insulation', 'insulation']);
    else if (lowerHay.includes('construction') || lowerHay.includes('cp -')) chosenSpec = findSpecialism(['construction']);
    if (!chosenSpec) chosenSpec = findSpecialism(['encon insulation']);
    if (chosenSpec && !businessSpecialism) setBusinessSpecialism(chosenSpec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, contractorRole, selectedContractorPackageId, selectedSubContractorPackageId, projectPackages, products, packageTypes]);

  // ─── API callers ───────────────────────────────────────────────────────
  const runAccountSearch = async (q) => {
    if (!q || q.trim().length < 2) return setAccounts([]);
    setAccountLoading(true);
    // Account-code-first: try resolving via the Phocas Customer Database's
    // Customer_Code bridge before falling back to the existing fuzzy name
    // search. Only auto-selects when a real code match is found — any
    // partial/no-match falls straight through to today's unchanged behaviour.
    try {
      const resolveRes = await api.get(`${API}/zoho/resolve-account-by-name`, { params: { customer_name: q } });
      if (resolveRes.data?.match_method === 'account_code' && resolveRes.data?.account?.id) {
        const matched = { ...resolveRes.data.account, matched_by_code: true };
        setAccounts([matched]);
        setSelectedAccount(matched);
        setAccountLoading(false);
        return;
      }
    } catch (_) { /* non-fatal — fall through to fuzzy name search below */ }
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q } });
      setAccounts(res.data.accounts || []);
    } catch (e) {
      toast.error('Failed to search Zoho accounts');
      setAccounts([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const runProjectSearch = async (q) => {
    if (!q || q.trim().length < 2) return setProjects([]);
    setProjectLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-projects`, { params: { q } });
      setProjects(res.data.projects || []);
    } catch (e) {
      toast.error('Failed to search Zoho projects');
      setProjects([]);
    } finally {
      setProjectLoading(false);
    }
  };

  const runContractorAccountSearch = async (q) => {
    if (!q || q.trim().length < 2) return setNewContractorAccounts([]);
    setNewContractorAccountLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q } });
      setNewContractorAccounts(res.data.accounts || []);
    } catch (e) {
      setNewContractorAccounts([]);
    } finally {
      setNewContractorAccountLoading(false);
    }
  };

  const loadProjectPackages = async (projectId) => {
    setPackagesLoading(true);
    try {
      const res = await api.get(`${API}/zoho/project/${projectId}/packages`);
      setProjectPackages(res.data || { contractor_packages: [], sub_contractor_packages: [] });
    } catch (e) {
      setProjectPackages({ contractor_packages: [], sub_contractor_packages: [] });
    } finally {
      setPackagesLoading(false);
    }
  };

  const loadPackageTypes = async () => {
    setPackageTypesLoading(true);
    try {
      const res = await api.get(`${API}/zoho/package-types`);
      setPackageTypes(res.data || { contractor_package_types: [], sub_contractor_package_types: [] });
    } catch (e) {
      toast.error('Failed to load package types — click "Retry" in the dropdown to try again.');
    } finally {
      setPackageTypesLoading(false);
    }
  };

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await api.get(`${API}/zoho/quote-products`);
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error('Failed to load Zoho products');
    } finally {
      setProductsLoading(false);
    }
  };

  const loadSuppliers = async (q) => {
    setSuppliersLoading(true);
    setSupplierError(null);
    // One automatic retry on 502/504/timeout — the deployed reverse-proxy
    // occasionally flakes on the first Zoho call of a new JWT cycle and a
    // 0.8s re-hit almost always succeeds.
    let res = null;
    let err = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        res = await api.get(`${API}/zoho/search-suppliers`, { params: { q: q || '' } });
        err = null;
        break;
      } catch (e) {
        err = e;
        const status = e?.response?.status;
        if (attempt === 0 && (status === 502 || status === 504 || !status)) {
          await new Promise(r => setTimeout(r, 800));
          continue;
        }
        break;
      }
    }
    if (err) {
      // Keep the previous supplier list around rather than blanking it —
      // avoids the "no results" flicker when the proxy hiccups.
      setSupplierError('Supplier search is temporarily unavailable — try again in a few seconds.');
      setSuppliersLoading(false);
      return;
    }
    try {
      const list = res?.data?.suppliers || [];
      setSuppliers(list);

      // Smart fallback only kicks in when the rep applied a system suggestion
      // (i.e. the query equals the suggested name) and Zoho returned nothing.
      // Step 1: try just the first word ("KNAUF DRYWALL" → "KNAUF").
      // Step 2: if that's also empty, clear the query and tell the rep.
      if (
        list.length === 0 &&
        supplierSuggestion?.name &&
        q && q.trim() === supplierSuggestion.name.trim() &&
        !supplierSearchRefined
      ) {
        const firstWord = supplierSuggestion.name.trim().split(/\s+/)[0];
        if (firstWord && firstWord.toLowerCase() !== supplierSuggestion.name.trim().toLowerCase()) {
          setSupplierSearchRefined(true);
          setSupplierQuery(firstWord);
          return; // The query change retriggers the debounced search.
        }
      }
      // Refined search also empty? Clear the box and surface the hint.
      if (
        list.length === 0 &&
        supplierSearchRefined &&
        supplierSuggestion?.name &&
        q && q.trim().toLowerCase() !== '' &&
        q.trim().toLowerCase() !== supplierSuggestion.name.trim().toLowerCase()
      ) {
        setSupplierSearchExhausted(true);
        setSupplierQuery('');
      }
    } finally {
      setSuppliersLoading(false);
    }
  };

  // Hit our own backend with the product codes from the quote so it can
  // peel off prefixes and reverse-lookup the C30 supplier group (e.g.,
  // codes starting with "17D"/"14D" → "ETEX INSULATION"). The result is
  // shown as a clickable suggestion above the Zoho supplier search.
  const fetchSupplierSuggestion = async () => {
    const items = effectiveForm?.line_items || effectiveForm?.items || [];
    const codes = items
      .map(it => (it?.product_code || it?.code || it?.sku || '').toString().trim())
      .filter(Boolean);
    if (codes.length === 0) {
      setSupplierSuggestion(null);
      return;
    }
    setSupplierSuggestionLoading(true);
    try {
      const res = await api.post(`${API}/zoho/suggest-supplier-from-codes`, { codes });
      if (res.data?.suggested_supplier_name) {
        setSupplierSuggestion({
          name: res.data.suggested_supplier_name,
          raw_name: res.data.raw_supplier_name,
          supplier_group_id: res.data.supplier_group_id,
          matched_prefix: res.data.matched_prefix,
          matched_code_count: res.data.matched_code_count,
          total_codes: res.data.total_codes,
          pag_name: res.data.pag_name,
          pag_category: res.data.pag_category || 'structural',
          user_confirmed: res.data.user_confirmed || false,
        });
      } else {
        setSupplierSuggestion(null);
      }
    } catch (e) {
      // non-fatal — user can still type their own
      setSupplierSuggestion(null);
    } finally {
      setSupplierSuggestionLoading(false);
    }
  };

  // Apply the suggestion: drop the suggested supplier name into the search
  // box. The existing debounced search on `supplierQuery` will then hit
  // Zoho. If the full name returns no hits we silently retry with just
  // the first word ("KNAUF DRYWALL" → "KNAUF") and surface a hint if even
  // that fails.
  const applySupplierSuggestion = () => {
    if (!supplierSuggestion?.name) return;
    setSupplierQuery(supplierSuggestion.name);
    setSupplierId('');
    setSupplierSuggestionDismissed(true);
    setSupplierAutoApplied(true);
    setSupplierSearchRefined(false);
    setSupplierSearchExhausted(false);
  };

  // Auto-select on perfect match: when the supplier list refreshes and exactly
  // one entry matches the current query (case-insensitive, trimmed), pre-pick
  // it. When the supplier was auto-applied from a suggestion and no exact match
  // exists, pick the first result (covers aliased/renamed supplier names).
  useEffect(() => {
    if (supplierId) return;                  // already chosen
    if (!supplierQuery || !supplierQuery.trim()) return;
    if (suppliersLoading) return;
    const q = supplierQuery.trim().toLowerCase();
    const exact = suppliers.filter(s => (s.name || '').trim().toLowerCase() === q);
    if (exact.length === 1) {
      handleSupplierPick(exact[0]);
    } else if (supplierAutoApplied && suppliers.length > 0) {
      // Auto-pick the first Zoho result when the suggestion was auto-applied
      // and there's no exact name match (e.g. "XTRATHERM" → "Unilin Insulation UK Ltd")
      handleSupplierPick(suppliers[0]);
      setSupplierAutoApplied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, suppliersLoading, supplierQuery]);

  // When the rep picks a supplier from the dropdown after applying a system
  // suggestion, save the chosen Zoho name back to the originating
  // manual_supplier_group so future quotes get a more accurate suggestion.
  // Skipped silently if there's no suggestion to learn from, or if the picked
  // name matches what we already have.
  const handleSupplierPick = (s) => {
    setSupplierId(s.id);
    setSupplierQuery(s.name);
    const sg_id = supplierSuggestion?.supplier_group_id;
    const original = (supplierSuggestion?.name || '').trim().toLowerCase();
    const picked = (s?.name || '').trim();
    if (sg_id && picked && picked.toLowerCase() !== original) {
      api.post(`${API}/zoho/learn-supplier-mapping`, {
        supplier_group_id: sg_id,
        crm_supplier_name: picked,
      }).catch(() => { /* non-fatal — silent learning */ });
    }
  };

  // Create a brand-new Zoho Prospect Account + linked Contact, then drop the
  // user straight into the Quote Details step with that new account selected.
  const createProspect = async () => {
    if (!prospectAccountName.trim()) return toast.error('Account name is required');
    if (!prospectAccountPhone.trim()) return toast.error('Account phone is required');
    if (!prospectContactLast.trim()) return toast.error('Contact last name is required');
    setCreatingProspect(true);
    try {
      const res = await api.post(`${API}/zoho/create-prospect-account`, {
        account_name: prospectAccountName.trim(),
        account_phone: prospectAccountPhone.trim(),
        contact_first_name: prospectContactFirst.trim() || null,
        contact_last_name: prospectContactLast.trim(),
        contact_email: prospectContactEmail.trim() || null,
        contact_mobile: prospectContactMobile.trim() || null,
      });
      const acct = res.data?.account;
      if (!acct?.id) throw new Error('Zoho did not return an account id');
      const newAccount = {
        id: acct.id,
        name: acct.name,
        city: null,
        country: null,
        customer_code: null,
      };
      // Surface the new account in the search list, mark it selected, and
      // collapse the create form so the user can hit Next: Quote Details.
      setAccounts(prev => [newAccount, ...prev.filter(a => a.id !== acct.id)]);
      setSelectedAccount(newAccount);
      setAccountQuery(acct.name);
      setShowProspectCreate(false);
      setProspectAccountName('');
      setProspectAccountPhone('');
      setProspectContactFirst('');
      setProspectContactLast('');
      setProspectContactEmail('');
      setProspectContactMobile('');
      toast.success('Prospect customer + contact created in Zoho');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to create prospect');
    } finally {
      setCreatingProspect(false);
    }
  };

  const createProject = async () => {
    if (!newProjectName.trim()) return toast.error('Project name is required');
    setCreatingProject(true);
    try {
      const res = await api.post(`${API}/zoho/create-project`, {
        deal_name: newProjectName.trim(),
        barbour_project_id: newProjectBarbourId.trim() || null,
        account_id: selectedAccount?.id || null,
        postcode: newProjectPostcode.trim() || null,
      });
      const p = {
        id: res.data.id,
        deal_name: res.data.deal_name,
        barbour_project_id: res.data.barbour_project_id,
        postcode: newProjectPostcode.trim() || null,
      };
      setSelectedProject(p);
      setShowProjectCreate(false);
      setProjects([p, ...projects]);
      toast.success('Project created in Zoho CRM');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  // "Search Barbour" (project step) — find nearby Barbour ABI projects by
  // postcode via the V2 route (Zoho function), using the super-admin
  // configured default radius (Settings > Integrations > Barbour).
  const searchBarbourForProject = async () => {
    if (!barbourPostcode.trim()) return;
    setBarbourSearching(true);
    setBarbourSearchError('');
    setBarbourResults(null);
    setCrmCheckStatus({});
    try {
      const body = { postcode: barbourPostcode.trim() };
      const r = parseFloat(barbourRadius);
      if (!isNaN(r) && r > 0) body.radius_miles = Math.min(r, barbourMaxRadius);
      const res = await api.post(`${API}/barbour/search-v2`, body);
      setBarbourResults(res.data?.projects || []);
    } catch (e) {
      setBarbourSearchError(e?.response?.data?.detail || 'Barbour search failed');
      toast.error(e?.response?.data?.detail || 'Barbour search failed');
    } finally {
      setBarbourSearching(false);
    }
  };

  // "Check CRM" for one Barbour result — priority order: Barbour ID first,
  // then postcode, then project name. Stops at the first method that finds
  // a VALIDATED match. Reuses the existing fuzzy /zoho/search-projects
  // endpoint (same one used by the normal project search above) for all 3
  // lookups, but — unlike the manual search box — this is an automated
  // "is this the same project?" decision, so each tier's top hit is
  // validated before being accepted (an unvalidated top hit can be a false
  // positive purely from shared substrings, e.g. "M4 5AB" vs "B64 5AB").
  const normPostcode = (s) => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
  const namesLikelyMatch = (a, b) => {
    const na = (a || '').trim().toLowerCase();
    const nb = (b || '').trim().toLowerCase();
    if (!na || !nb) return false;
    if (na === nb) return true;
    const wordsA = new Set(na.split(/[^a-z0-9]+/).filter((w) => w.length >= 3));
    const wordsB = new Set(nb.split(/[^a-z0-9]+/).filter((w) => w.length >= 3));
    if (wordsA.size === 0 || wordsB.size === 0) return false;
    let overlap = 0;
    wordsA.forEach((w) => { if (wordsB.has(w)) overlap += 1; });
    return (overlap / Math.min(wordsA.size, wordsB.size)) >= 0.6;
  };
  const checkCrmForBarbourResult = async (result) => {
    const key = result.barbour_id || result.project_name;
    setCrmCheckStatus(prev => ({ ...prev, [key]: { checking: true } }));
    try {
      let found = null;
      if (result.barbour_id) {
        const r = await api.get(`${API}/zoho/search-projects`, { params: { q: String(result.barbour_id) } });
        found = (r.data?.projects || []).find(
          p => (p.barbour_project_id || '').trim().toLowerCase() === String(result.barbour_id).trim().toLowerCase()
        ) || null;
      }
      if (!found && result.postcode) {
        const r = await api.get(`${API}/zoho/search-projects`, { params: { q: result.postcode } });
        found = (r.data?.projects || []).find(
          p => p.postcode && normPostcode(p.postcode) === normPostcode(result.postcode)
        ) || null;
      }
      if (!found && result.project_name) {
        const r = await api.get(`${API}/zoho/search-projects`, { params: { q: result.project_name } });
        found = (r.data?.projects || []).find(
          p => namesLikelyMatch(p.deal_name, result.project_name) || namesLikelyMatch(p.barbour_project_name, result.project_name)
        ) || null;
      }
      setCrmCheckStatus(prev => ({ ...prev, [key]: { checking: false, found } }));
    } catch (e) {
      setCrmCheckStatus(prev => ({ ...prev, [key]: { checking: false, found: null, error: true } }));
      toast.error('Failed to check CRM');
    }
  };

  // Existing CRM match found for a Barbour result — select it exactly like
  // picking a normal search result, and collapse the Barbour panel.
  const selectCrmMatchFromBarbour = (project) => {
    setSelectedProject(project);
    setShowBarbourSearch(false);
  };

  // No CRM match for a Barbour result — jump into "Create new project",
  // pre-filled with the Barbour project's name, postcode and ID, so the
  // user can continue adding the quote to the project as normal.
  const addBarbourResultToCrm = (result) => {
    setNewProjectName(result.project_name || '');
    setNewProjectPostcode(result.postcode || '');
    setNewProjectBarbourId(result.barbour_id ? String(result.barbour_id) : '');
    setShowProjectCreate(true);
    setShowBarbourSearch(false);
  };

  const createPackage = async () => {
    if (!newContractorAccount?.id) return toast.error('Pick a contractor account');
    if (!newPackageType) return toast.error('Pick a package type');
    if (!selectedProject?.id) return toast.error('Project is required');
    setCreatingPackage(true);
    try {
      const endpoint = contractorRole === 'main' ? 'create-contractor-package' : 'create-subcontractor-package';
      const payload = contractorRole === 'main'
        ? {
            project_id: selectedProject.id,
            contractor_account_id: newContractorAccount.id,
            package_type: newPackageType,
            encon_start_date: newPackageEnconStartDate || null,
          }
        : {
            project_id: selectedProject.id,
            sub_contractor_account_id: newContractorAccount.id,
            sub_contractor_package_type: newPackageType,
            parent_contractor_package_id: newPackageParentCP || null,
            package_categories: newPackageCategories.length > 0 ? newPackageCategories : null,
            encon_start_date: newPackageEnconStartDate || null,
          };
      const res = await api.post(`${API}/zoho/${endpoint}`, payload);
      // Optimistically append the new package to local state so UI shows it even though
      // Zoho's search index can lag ~30-60s. We then also re-fetch (will replace once indexed).
      const newPkg = contractorRole === 'main'
        ? {
            id: res.data.id,
            name: res.data.name,
            contractor_account_id: newContractorAccount.id,
            contractor_account_name: newContractorAccount.name,
            package_type: newPackageType,
            encon_start_date: newPackageEnconStartDate || null,
            status: null,
            amount: null,
            closing_date: null,
          }
        : {
            id: res.data.id,
            name: res.data.name,
            sub_contractor_account_id: newContractorAccount.id,
            sub_contractor_account_name: newContractorAccount.name,
            package_type: newPackageType,
            package_category: [],
            encon_start_date: newPackageEnconStartDate || null,
            status: null,
            amount: null,
            parent_contractor_package_id: newPackageParentCP || null,
            parent_contractor_package_name: newPackageParentCP
              ? (projectPackages.contractor_packages.find(p => p.id === newPackageParentCP)?.name || null)
              : null,
          };
      setProjectPackages(prev => ({
        contractor_packages: contractorRole === 'main' ? [newPkg, ...prev.contractor_packages] : prev.contractor_packages,
        sub_contractor_packages: contractorRole === 'sub' ? [newPkg, ...prev.sub_contractor_packages] : prev.sub_contractor_packages,
      }));
      if (contractorRole === 'main') setSelectedContractorPackageId(res.data.id);
      else setSelectedSubContractorPackageId(res.data.id);
      setShowNewPackage(false);
      setNewContractorAccount(null);
      setNewContractorAccountQuery('');
      setNewPackageType('');
      setNewPackageParentCP('');
      setNewPackageCategories([]);
      setNewPackageEnconStartDate(selectedProject?.barbour_start_date || '');
      toast.success(`${contractorRole === 'main' ? 'Main' : 'Sub'} contractor package created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create package');
    } finally {
      setCreatingPackage(false);
    }
  };

  const handleSubmit = async (opts = {}) => {
    const chainAfter = !!opts.chainCall;
    if (!effectiveAccount?.id) {
      return toast.error(mode === 'project' ? 'Pick a contractor & package first' : 'Pick a customer account first');
    }
    if (!productId) return toast.error('Select a business specialism');
    if (!subject?.trim()) return toast.error('Subject is required');

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('account_id', effectiveAccount.id);
      form.append('product_id', productId);
      form.append('supplier_id', supplierId || '');
      form.append('subject', subject.trim());
      form.append('list_price', String(parseFloat(grandTotal || effectiveSubtotal || 0) || 0));
      form.append('contribution', String(parseFloat(contribution || 0) || 0));
      form.append('follow_up_days', String(followUpDays ?? 3));
      form.append('quote_raised_date', todayISO);
      if (businessSpecialism) form.append('business_specialism', businessSpecialism);
      if (packageClosingDate && (selectedContractorPackageId || selectedSubContractorPackageId)) {
        form.append('package_closing_date', packageClosingDate);
      }
      if (effectiveForm?.id) form.append('local_quote_id', effectiveForm.id);
      form.append('quote_stage', quoteStage || 'Negotiation Tender');
      if (effectivePdf) form.append('pdf_file', effectivePdf);
      // Always send a JSON snapshot of the live quote form. Backend uses
      // this to regenerate the PDF from the very latest edits when no PDF
      // file was uploaded — covers the "Create Quote" and "Recent Quotes"
      // flows where the user never explicitly uploads a PDF.
      if (effectiveForm) {
        try { form.append('quote_data_json', JSON.stringify(effectiveForm)); } catch (_) { /* non-fatal */ }
      }
      if (selectedProject?.id) form.append('deal_id', selectedProject.id);
      if (selectedContractorPackageId) form.append('contractor_package_id', selectedContractorPackageId);
      if (selectedSubContractorPackageId) form.append('sub_contractor_package_id', selectedSubContractorPackageId);
      if (selectedContact?.id) form.append('contact_id', selectedContact.id);

      // Build product description from line items: "DESCRIPTION - QUANTITY" per line
      const itemsForDesc = effectiveForm?.line_items || effectiveForm?.items || [];
      const descLines = itemsForDesc
        .map(it => {
          const desc = (it?.description || it?.product_name || it?.name || '').trim();
          const qty = parseFloat(it?.quantity) || 0;
          if (!desc) return null;
          return qty > 0 ? `${desc} - ${qty % 1 === 0 ? qty.toFixed(0) : qty}` : desc;
        })
        .filter(Boolean);
      if (descLines.length > 0) {
        form.append('product_description', descLines.join('\n'));
      }

      const res = await api.post(`${API}/zoho/create-quote`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success('Quote created in Zoho CRM');
      // Signal the dashboard card to refresh immediately so the new quote
      // appears in the Quotes/Orders count without waiting for the next poll.
      window.dispatchEvent(new Event('c30:quotes-changed'));
      if (res.data.quote_id) {
        const localId = quoteForm?._localItemId || quoteForm?.id;
        if (localId) {
          try {
            await api.post(`${API}/zoho/link-quote`, {
              local_quote_id: localId,
              zoho_quote_id: res.data.quote_id,
              zoho_quote_url: res.data.quote_url,
              subject: subject?.trim() || null,
              supplier_id: supplierId || null,
              supplier_name: supplierId ? (supplierQuery || null) : null,
              deal_id: selectedProject?.id || null,
              deal_name: selectedProject?.deal_name || null,
              contractor_package_id: selectedContractorPackageId || null,
              sub_contractor_package_id: selectedSubContractorPackageId || null,
              quote_stage: quoteStage || 'Negotiation Tender',
            });
          } catch (_) { /* non-fatal */ }
        }
      }
      // Notify parent so the QuoteCard can update its "View in CRM" / blue-tick state.
      if (onCrmSuccess && res.data.quote_url) {
        onCrmSuccess(res.data.quote_url);
      }
      // Per user request — DON'T auto-open Zoho. The success panel below
      // shows an "Open in Zoho" link if they want to view it; the default
      // action after creating a quote is to stay on the dashboard so the
      // user can pick "Add Call" or close. (Previous behaviour:
      // window.open(quote_url) on every successful create.)
      if (chainAfter && onChainToCall) {
        // Chain → close this quote modal and ask the parent to open the
        // Add-Call modal pre-filled with the same account/contact. Spares
        // the rep from re-picking the customer.
        if (modalUiV2) activityBar.discardActive();
        onClose?.();
        onChainToCall({
          account: effectiveAccount,
          contact: selectedContact ? { id: selectedContact.id, name: selectedContact.full_name || selectedContact.name } : null,
        });
        return;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object' && detail?.code === 'NO_ZOHO_USER') {
        setAccessRequestEmail(detail.email || '');
        setShowAccessRequest(true);
        return;
      }
      toast.error((typeof detail === 'string' ? detail : detail?.message) || e.message || 'Failed to create Zoho quote');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAccessRequest = async () => {
    setAccessRequestSending(true);
    try {
      await api.post(`${API}/zoho/request-access`, {
        reason: accessRequestReason || '',
        context: (quoteForm?.notes || quoteForm?.quote_number) ? `Quote: ${quoteForm?.notes || quoteForm?.quote_number}` : '',
      });
      setAccessRequestSent(true);
      toast.success('Access request sent to the CRM admin');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send request');
    } finally {
      setAccessRequestSending(false);
    }
  };

  const handleAddQuoteContact = async () => {
    const last = (qNewLast || '').trim();
    if (!last || !effectiveAccount?.id) return;
    setAddingQuoteContact(true);
    try {
      const res = await api.post(`${API}/zoho/create-contact`, {
        account_id: effectiveAccount.id,
        first_name: qNewFirst.trim() || null,
        last_name: last,
        phone: qNewPhone.trim() || null,
        email: qNewEmail.trim() || null,
        title: qNewTitle.trim() || null,
      });
      const c = res.data?.contact;
      if (c?.id) {
        const newC = {
          id: c.id,
          full_name: c.full_name || `${qNewFirst} ${last}`.trim(),
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
          phone: c.phone,
          title: c.title,
        };
        setContacts(prev => [...prev, newC]);
        setSelectedContact(newC);
      }
      setShowQuoteAddContact(false);
      setQNewFirst(''); setQNewLast(''); setQNewPhone(''); setQNewEmail(''); setQNewTitle('');
      toast.success('Contact added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add contact');
    } finally {
      setAddingQuoteContact(false);
    }
  };

  // Does step 3 have a valid selection (or did user skip with "no project")?
  const step3Ready = useMemo(() => {
    if (!isProject) return true; // No project = skip directly to step 4
    if (!selectedProject) return false;
    if (contractorRole === 'main') return !!selectedContractorPackageId;
    return !!selectedSubContractorPackageId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProject, selectedProject, contractorRole, selectedContractorPackageId, selectedSubContractorPackageId]);

  // In project mode the account on the Zoho Quote IS the contractor on the
  // chosen package (no separate Customer step). In quote-only mode, the user
  // explicitly picks the account.
  const effectiveAccount = useMemo(() => {
    if (mode === 'quote') return selectedAccount;
    if (mode === 'project') {
      if (selectedContractorPackageId) {
        const pkg = projectPackages.contractor_packages.find(p => p.id === selectedContractorPackageId);
        if (pkg?.contractor_account_id) return { id: pkg.contractor_account_id, name: pkg.contractor_account_name || pkg.name };
      }
      if (selectedSubContractorPackageId) {
        const pkg = projectPackages.sub_contractor_packages.find(p => p.id === selectedSubContractorPackageId);
        if (pkg?.sub_contractor_account_id) return { id: pkg.sub_contractor_account_id, name: pkg.sub_contractor_account_name || pkg.name };
      }
    }
    return null;
  }, [mode, selectedAccount, selectedContractorPackageId, selectedSubContractorPackageId, projectPackages]);

  // Auto-load contacts when the effective account resolves. Clears the
  // previously selected contact & list so stale data never shows.
  // Placed AFTER the effectiveAccount useMemo to avoid TDZ errors.
  useEffect(() => {
    const acctId = effectiveAccount?.id;
    if (!acctId) { setContacts([]); setSelectedContact(null); return; }
    let cancelled = false;
    setContactsLoading(true);
    setContacts([]);
    setSelectedContact(null);
    api.get(`${API}/zoho/account/${acctId}/contacts`)
      .then((res) => {
        if (!cancelled) {
          const loadedContacts = res.data.contacts || [];
          setContacts(loadedContacts);
          // Auto-select the first contact as default
          if (loadedContacts.length > 0) setSelectedContact(loadedContacts[0]);
        }
      })
      .catch(() => { if (!cancelled) setContacts([]); })
      .finally(() => { if (!cancelled) setContactsLoading(false); });
    return () => { cancelled = true; };
    // effectiveAccount is a derived value — track its id to avoid stale closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAccount?.id]);

  if (!isOpen) return null;

  const stepLabels = mode === 'project'
    ? ['1. Project', '2. Contractor & Package', '3. Quote Details']
    : mode === 'quote'
      ? ['1. Account', '2. Quote Details']
      : []; // step 0 — no stepper

  // Whether the fast-track dropzone is currently the active interaction —
  // when true, dragging a PDF anywhere in the modal counts as "into the
  // dropzone" so the rep doesn't have to aim precisely on small screens.
  const fastTrackDropzoneActive = fastTrack && (
    (mode === 'quote' && step === 1 && !localQuoteForm) ||
    (mode === 'project' && step === 3 && projectQuotedChoice === 'yes' && !localQuoteForm)
  );

  return (
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 ${modalV2OverlayClass(modalUiV2)}`}
      style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex }}
      onDragOver={fastTrackDropzoneActive ? (e) => { e.preventDefault(); setPdfDragOver(true); } : undefined}
      onDragLeave={fastTrackDropzoneActive ? () => setPdfDragOver(false) : undefined}
      onDrop={fastTrackDropzoneActive ? handleFastTrackDrop : undefined}
    >
      <div className="bg-gradient-to-br from-blue-50/40 via-white to-blue-50/20 rounded-3xl shadow-2xl w-full max-w-3xl overflow-y-auto border border-gray-200" style={{ maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '92vh' }}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 bg-white/60 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm bg-gradient-to-br from-blue-600 to-blue-500">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Add Quote to Zoho CRM</h2>
              <p className="text-xs text-gray-500">Link a customer, project and contractor package in one go</p>
            </div>
          </div>
          <button onClick={() => { if (modalUiV2) activityBar.discardActive(); onClose?.(); }} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100" data-testid="add-to-crm-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!showAccessRequest && (
          <div className="flex flex-wrap items-center gap-2 px-6 pt-4 text-xs text-gray-500">
            {stepLabels.map((label, i) => (
              <span key={label} className={step > i ? 'text-blue-700 font-bold' : step === i + 1 ? 'text-blue-700 font-bold' : ''}>
                {label}
                {i < stepLabels.length - 1 && <span className="mx-1 text-gray-400">›</span>}
              </span>
            ))}
          </div>
        )}

        <div className="p-6 space-y-4">
          {/* ───── Request-access view (NO_ZOHO_USER) ───── */}
          {showAccessRequest && !accessRequestSent && (
            <div className="space-y-3">
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-bold text-amber-900 mb-1">Zoho CRM access not set up</div>
                    <div className="text-amber-800">
                      Your C30 Dashboard login (<code className="font-mono bg-white px-1 py-0.5 rounded text-xs">{accessRequestEmail}</code>)
                      isn't linked to a Zoho CRM user, so quotes can't be uploaded to CRM under your name yet.
                      The quote is still saved on the dashboard as normal.
                    </div>
                  </div>
                </div>
              </div>
              <Label className="text-sm font-semibold">Reason / context for your request (optional)</Label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="e.g. I handle Bham quotes and need to add them to CRM"
                value={accessRequestReason}
                onChange={(e) => setAccessRequestReason(e.target.value)}
                data-testid="access-request-reason"
              />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowAccessRequest(false)}>Cancel</Button>
                <Button onClick={submitAccessRequest} disabled={accessRequestSending} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="access-request-submit">
                  {accessRequestSending ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</>) : (<><Building2 className="w-4 h-4 mr-2" />Request Access</>)}
                </Button>
              </div>
            </div>
          )}

          {showAccessRequest && accessRequestSent && (
            <div className="space-y-3 py-2">
              <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-bold text-emerald-900 mb-1">Request sent</div>
                  <div className="text-emerald-800">The CRM admin has been notified.</div>
                </div>
              </div>
              <div className="flex justify-end"><Button variant="outline" onClick={onClose} data-testid="access-request-close">Close</Button></div>
            </div>
          )}

          {/* ───── Step 0: Mode picker — extracted to AddToCrm/StepModePicker.jsx ───── */}
          <StepModePicker
            step={step}
            showAccessRequest={showAccessRequest}
            setMode={setMode}
            setStep={setStep}
            setProjectQuery={setProjectQuery}
            quoteForm={quoteForm}
            onClose={onClose}
          />

          {/* ───── Step 1 (quote mode) — extracted to AddToCrm/StepAccountQuote.jsx ───── */}
          <StepAccountQuote
            step={step} mode={mode} showAccessRequest={showAccessRequest}
            fastTrack={fastTrack} localQuoteForm={localQuoteForm}
            pdfDragOver={pdfDragOver} setPdfDragOver={setPdfDragOver}
            handleFastTrackDrop={handleFastTrackDrop}
            handleFastTrackBrowse={handleFastTrackBrowse}
            fastTrackInputRef={fastTrackInputRef} pdfParsing={pdfParsing}
            accountQuery={accountQuery} setAccountQuery={setAccountQuery}
            accountLoading={accountLoading} accounts={accounts}
            selectedAccount={selectedAccount} setSelectedAccount={setSelectedAccount}
            showProspectCreate={showProspectCreate} setShowProspectCreate={setShowProspectCreate}
            prospectAccountName={prospectAccountName} setProspectAccountName={setProspectAccountName}
            prospectAccountPhone={prospectAccountPhone} setProspectAccountPhone={setProspectAccountPhone}
            prospectContactFirst={prospectContactFirst} setProspectContactFirst={setProspectContactFirst}
            prospectContactLast={prospectContactLast} setProspectContactLast={setProspectContactLast}
            prospectContactEmail={prospectContactEmail} setProspectContactEmail={setProspectContactEmail}
            prospectContactMobile={prospectContactMobile} setProspectContactMobile={setProspectContactMobile}
            creatingProspect={creatingProspect} createProspect={createProspect}
            runAccountSearch={runAccountSearch} quoteForm={quoteForm} setStep={setStep}
            onSelectRecentQuote={handleSelectRecentQuote}
            onHistoryClick={item => setHistoryItem(item)}
          />

          {/* ───── Step 1 (project mode) — extracted to AddToCrm/StepProject.jsx ───── */}
          <StepProject
            step={step} mode={mode} showAccessRequest={showAccessRequest}
            projectQuery={projectQuery} setProjectQuery={setProjectQuery}
            projectLoading={projectLoading} projects={projects}
            selectedProject={selectedProject} setSelectedProject={setSelectedProject}
            showProjectCreate={showProjectCreate} setShowProjectCreate={setShowProjectCreate}
            newProjectName={newProjectName} setNewProjectName={setNewProjectName}
            newProjectBarbourId={newProjectBarbourId} setNewProjectBarbourId={setNewProjectBarbourId}
            newProjectPostcode={newProjectPostcode} setNewProjectPostcode={setNewProjectPostcode}
            creatingProject={creatingProject} runProjectSearch={runProjectSearch}
            createProject={createProject} quoteForm={quoteForm} setStep={setStep}
            showBarbourSearch={showBarbourSearch} setShowBarbourSearch={setShowBarbourSearch}
            barbourPostcode={barbourPostcode} setBarbourPostcode={setBarbourPostcode}
            barbourRadius={barbourRadius} setBarbourRadius={setBarbourRadius} barbourMaxRadius={barbourMaxRadius}
            barbourSearching={barbourSearching} barbourResults={barbourResults}
            barbourSearchError={barbourSearchError} searchBarbourForProject={searchBarbourForProject}
            crmCheckStatus={crmCheckStatus} checkCrmForBarbourResult={checkCrmForBarbourResult}
            selectCrmMatchFromBarbour={selectCrmMatchFromBarbour} addBarbourResultToCrm={addBarbourResultToCrm}
            onProjectsUpdate={(projectId, patch) => {
              setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...patch } : p));
              // Also update selected project if it's the same one
              if (selectedProject?.id === projectId) {
                setSelectedProject(prev => prev ? { ...prev, ...patch } : prev);
              }
            }}
          />

          {/* ───── Step 2 (project mode) — extracted to AddToCrm/StepPackage.jsx ───── */}
          <StepPackage
            step={step} mode={mode} showAccessRequest={showAccessRequest}
            selectedProject={selectedProject}
            effectiveForm={effectiveForm}
            contractorRole={contractorRole} setContractorRole={setContractorRoleByUser}
            selectedContractorPackageId={selectedContractorPackageId} setSelectedContractorPackageId={setSelectedContractorPackageId}
            selectedSubContractorPackageId={selectedSubContractorPackageId} setSelectedSubContractorPackageId={setSelectedSubContractorPackageId}
            selectedContractorAccountId={selectedContractorAccountId} setSelectedContractorAccountId={setSelectedContractorAccountId}
            showNewPackage={showNewPackage} setShowNewPackage={setShowNewPackage}
            newContractorAccountQuery={newContractorAccountQuery} setNewContractorAccountQuery={setNewContractorAccountQuery}
            newContractorAccount={newContractorAccount} setNewContractorAccount={setNewContractorAccount}
            newContractorAccounts={newContractorAccounts} newContractorAccountLoading={newContractorAccountLoading}
            newPackageType={newPackageType} setNewPackageType={setNewPackageType}
            newPackageParentCP={newPackageParentCP} setNewPackageParentCP={setNewPackageParentCP}
            newPackageCategories={newPackageCategories} setNewPackageCategories={setNewPackageCategories}
            newPackageEnconStartDate={newPackageEnconStartDate} setNewPackageEnconStartDate={setNewPackageEnconStartDate}
            packagesLoading={packagesLoading} projectPackages={projectPackages} packageTypes={packageTypes}
            packageTypesLoading={packageTypesLoading} onRetryPackageTypes={loadPackageTypes}
            mainContractorCount={mainContractorCount} subContractorCount={subContractorCount}
            creatingPackage={creatingPackage} createPackage={createPackage}
            step3Ready={step3Ready} setStep={setStep}
          />

          {/* ───── Final step: Quote Details — step 2 in quote mode, step 3 in project mode ───── */}
          {((step === 3 && mode === 'project') || (step === 2 && mode === 'quote')) && !showAccessRequest && !result?.quote_id && (
            <>
              {/* Project fast-track gate — once contractor & package are picked,
                  ask if there's a quote to attach. Stays open until either
                  "No" is chosen (modal closes) OR "Yes" is chosen AND a PDF
                  has been parsed (then the full Quote Details body below
                  takes over). */}
              {fastTrack && mode === 'project' && (projectQuotedChoice === null || (projectQuotedChoice === 'yes' && !localQuoteForm)) && (
                <div className="space-y-3" data-testid="crm-fasttrack-project-quoted-gate">
                  <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/50 p-4 text-sm">
                    <div className="font-semibold text-gray-900 mb-1">Has this package been quoted?</div>
                    <div className="text-gray-600 text-xs">If yes, drop the quote PDF below to upload it under this package. If no, we&apos;ll save the project &amp; package and stop here — no Zoho quote will be created.</div>
                  </div>
                  {projectQuotedChoice === null && (
                    <div className="grid grid-cols-2 gap-3">
                      <Button onClick={() => setProjectQuotedChoice('yes')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="crm-fasttrack-quoted-yes">Yes — add a quote PDF</Button>
                      <Button variant="outline" onClick={() => { toast.success('Project & package saved in Zoho'); onClose(); }} data-testid="crm-fasttrack-quoted-no">No — save project only</Button>
                    </div>
                  )}
                  {projectQuotedChoice === 'yes' && !localQuoteForm && (
                    <>
                      {/* ── Recent quotes picker above the project PDF drop ── */}
                      <RecentQuotesPicker onSelect={handleSelectRecentQuote} onCustomerNameClick={name => setCustomerSearchName(name)} onHistoryClick={item => setHistoryItem(item)} />

                      {/* Divider */}
                      <div className="relative flex items-center gap-2">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-[11px] text-gray-400 font-medium">or upload a new PDF</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>

                      <div
                        onDragOver={(e) => { e.preventDefault(); setPdfDragOver(true); }}
                        onDragLeave={() => setPdfDragOver(false)}
                        onDrop={handleFastTrackDrop}
                        className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${pdfDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
                        data-testid="crm-fasttrack-project-dropzone"
                      >
                        <input ref={fastTrackInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleFastTrackBrowse} />
                        {pdfParsing ? (
                          <div className="flex flex-col items-center gap-2 text-sm text-gray-700">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                            Parsing PDF…
                          </div>
                        ) : (
                          <>
                            <div className="font-semibold text-gray-900">Drag &amp; drop the quote PDF</div>
                            <div className="text-xs text-gray-500 mt-1">or</div>
                            <Button onClick={() => fastTrackInputRef.current?.click()} className="mt-2 bg-blue-600 hover:bg-blue-700 text-white">Browse files</Button>
                          </>
                        )}
                      </div>
                      <div className="flex justify-between gap-2">
                        <Button variant="outline" onClick={() => setProjectQuotedChoice(null)}>← Back</Button>
                        <Button variant="ghost" onClick={() => { toast.success('Project & package saved in Zoho'); onClose(); }}>Skip — save without quote</Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Normal Quote Details body — hidden until the project fast-track
                  gate has resolved with a parsed PDF. */}
              {(!fastTrack || mode !== 'project' || (projectQuotedChoice === 'yes' && localQuoteForm)) && (
              <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <div className="text-[11px] uppercase tracking-wider text-blue-700 font-bold">Customer</div>
                <div className="font-semibold">{effectiveAccount?.name || '—'}</div>
                {selectedProject && (
                  <>
                    <div className="text-[11px] uppercase tracking-wider text-blue-700 font-bold mt-2">Project</div>
                    <div className="text-xs">{selectedProject.deal_name}{selectedProject.barbour_project_id ? ` · Barbour #${selectedProject.barbour_project_id}` : ''}</div>
                  </>
                )}
                {isProject && (selectedContractorPackageId || selectedSubContractorPackageId) && (
                  <>
                    <div className="text-[11px] uppercase tracking-wider text-blue-700 font-bold mt-2">
                      Package ({contractorRole === 'main' ? 'Main' : 'Sub'} Contractor)
                    </div>
                    <div className="text-xs">
                      {contractorRole === 'main'
                        ? (() => {
                            const pkg = projectPackages.contractor_packages.find(p => p.id === selectedContractorPackageId);
                            return pkg ? `${pkg.contractor_account_name || pkg.name} — ${pkg.package_type || ''}` : '—';
                          })()
                        : (() => {
                            const pkg = projectPackages.sub_contractor_packages.find(p => p.id === selectedSubContractorPackageId);
                            return pkg ? `${pkg.sub_contractor_account_name || pkg.name} — ${pkg.package_type || ''}` : '—';
                          })()}
                    </div>
                  </>
                )}
              </div>

              {/* ── Contact picker ─────────────────────────────────────── */}
              {effectiveAccount && (
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                    <UserRound className="w-3.5 h-3.5" /> Contact
                    {selectedContact && (
                      <span className="ml-auto text-[11px] bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-bold">
                        {selectedContact.full_name}
                      </span>
                    )}
                  </Label>
                  {contactsLoading ? (
                    <div className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading contacts…
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="text-xs text-gray-500 mt-2">No contacts on this account in Zoho.</div>
                  ) : (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                      {contacts.map((c) => {
                        const checked = selectedContact?.id === c.id;
                        return (
                          <label
                            key={c.id}
                            className={`flex items-start gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-all ${
                              checked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            data-testid={`quote-contact-option-${c.id}`}
                          >
                            <input
                              type="radio"
                              name="quote_contact"
                              checked={checked}
                              onChange={() => setSelectedContact(checked ? null : c)}
                              onClick={() => { if (checked) setSelectedContact(null); }}
                              className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="font-semibold text-sm text-gray-900 truncate">
                                {c.full_name || c.email || c.id}
                              </div>
                              {(c.title || c.email) && (
                                <div className="text-[11px] text-gray-500 truncate">
                                  {[c.title, c.email].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* Add new contact mini-form */}
                  {!showQuoteAddContact ? (
                    <button
                      type="button"
                      onClick={() => setShowQuoteAddContact(true)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
                      data-testid="quote-contact-add-toggle"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Add new contact to this account
                    </button>
                  ) : (
                    <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="quote-contact-add-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input value={qNewFirst} onChange={(e) => setQNewFirst(e.target.value)} placeholder="First name" className="rounded-lg border-2 border-gray-300 bg-white h-9 text-sm" data-testid="quote-new-contact-first" />
                        <Input value={qNewLast} onChange={(e) => setQNewLast(e.target.value)} placeholder="Last name *" className="rounded-lg border-2 border-gray-300 bg-white h-9 text-sm" data-testid="quote-new-contact-last" />
                        <Input value={qNewPhone} onChange={(e) => setQNewPhone(e.target.value)} placeholder="Phone" className="rounded-lg border-2 border-gray-300 bg-white h-9 text-sm" data-testid="quote-new-contact-phone" />
                        <Input value={qNewEmail} onChange={(e) => setQNewEmail(e.target.value)} placeholder="Email" className="rounded-lg border-2 border-gray-300 bg-white h-9 text-sm" data-testid="quote-new-contact-email" />
                        <Input value={qNewTitle} onChange={(e) => setQNewTitle(e.target.value)} placeholder="Job title" className="rounded-lg border-2 border-gray-300 bg-white h-9 text-sm sm:col-span-2" data-testid="quote-new-contact-title" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowQuoteAddContact(false)} className="rounded-xl h-9 px-3 text-xs" data-testid="quote-new-contact-cancel">Cancel</Button>
                        <Button type="button" onClick={handleAddQuoteContact} disabled={addingQuoteContact || !qNewLast.trim()} className="rounded-xl h-9 px-3 text-xs text-white font-semibold bg-blue-600 hover:bg-blue-700" data-testid="quote-new-contact-save">
                          {addingQuoteContact ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Saving…</> : 'Add contact'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* ────────────────────────────────────────────────────────── */}

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-1">
                  <Label className="text-sm font-semibold">Subject *</Label>
                  <Input
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setSubjectAutoGenerated(false); // user is now editing manually
                    }}
                    placeholder="e.g. Bham Project"
                    className={`mt-1 ${INPUT_CLS}`}
                    data-testid="crm-subject-input"
                  />
                </div>
                {/* ─── Quote Value ──────────────────────────────────────── */}
                <div className="col-span-1">
                  <Label className="text-sm font-semibold">
                    Quote Value (£) *
                    {fastTrack && localQuoteForm && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        parsed from PDF — confirm or amend
                      </span>
                    )}
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm pointer-events-none">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={grandTotal}
                      onChange={(e) => setGrandTotal(e.target.value)}
                      placeholder="0.00"
                      className={`pl-7 ${INPUT_CLS}`}
                      data-testid="crm-grand-total-input"
                    />
                  </div>
                  {!grandTotal && (
                    <p className="text-xs text-amber-600 mt-0.5">Required — enter the quote grand total</p>
                  )}
                </div>
                <div className="col-span-1">
                  <Label className="text-sm font-semibold">Product *</Label>
                  <Select value={productId} onValueChange={setProductId}>
                    <SelectTrigger className={`mt-1 ${INPUT_CLS}`} data-testid="crm-product-select">
                      <SelectValue placeholder={productsLoading ? 'Loading…' : 'Choose a product'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[120] rounded-xl border-gray-200 shadow-xl">
                      {products.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500 flex items-center justify-between gap-2">
                          <span>{productsLoading ? 'Loading…' : 'No products loaded'}</span>
                          {!productsLoading && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); loadProducts(); }} className="text-blue-600 underline text-xs" data-testid="crm-product-retry">Retry</button>
                          )}
                        </div>
                      ) : products.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`crm-product-option-${p.id}`}>
                          <ColourDot color={productColour(p.name)} />{p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1">
                  <Label className="text-sm font-semibold">Business Specialism</Label>
                  <Select value={businessSpecialism} onValueChange={setBusinessSpecialism}>
                    <SelectTrigger className={`mt-1 ${INPUT_CLS}`} data-testid="crm-specialism-select">
                      <SelectValue placeholder="Choose a business specialism (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[120] rounded-xl border-gray-200 shadow-xl">
                      {(() => {
                        const meta = packageTypes.quote_business_specialisms_meta || [];
                        const colourFor = (v) => (meta.find(m => m.value === v) || {}).color || null;
                        const items = (packageTypes.quote_business_specialisms || []).filter(s => s && s !== '-None-');
                        if (items.length === 0) {
                          return (
                            <div className="px-3 py-2 text-xs text-gray-500 flex items-center justify-between gap-2">
                              <span>{packageTypesLoading ? 'Loading…' : 'No specialisms loaded'}</span>
                              {!packageTypesLoading && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); loadPackageTypes(); }} className="text-blue-600 underline text-xs" data-testid="crm-specialism-retry">Retry</button>
                              )}
                            </div>
                          );
                        }
                        return items.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`crm-specialism-option-${s.replace(/\s+/g, '-').toLowerCase()}`}>
                            <ColourDot color={colourFor(s)} />{s}
                          </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-semibold">Supplier</Label>

                  {/* Supplier is auto-applied from the suggestion — confirmation card appears automatically. */}

                  <Input
                    value={supplierQuery}
                    onChange={(e) => {
                      setSupplierQuery(e.target.value);
                      setSupplierId('');
                      setSupplierAutoApplied(false); // User is now typing manually
                      // User started typing again — clear smart-search flags
                      // so the auto-refine doesn't fire on a manual query.
                      setSupplierSearchRefined(false);
                      setSupplierSearchExhausted(false);
                    }}
                    placeholder="Search supplier (e.g. British Gypsum)"
                    className={`mt-1 ${INPUT_CLS}`}
                    data-testid="crm-supplier-input"
                  />

                  {/* Suggestion chip — shown when system found a likely supplier but
                      confidence wasn't high enough to auto-apply. User must click to
                      accept or dismiss. */}
                  {supplierSuggestion?.name && !supplierSuggestionDismissed && !supplierId && !supplierAutoApplied && !supplierQuery.trim() && (
                    <div
                      className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center justify-between gap-2"
                      data-testid="crm-supplier-suggestion-chip"
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-amber-900">Suggested supplier</p>
                          <p className="text-sm font-bold text-amber-800">{supplierSuggestion.name}</p>
                          <p className="text-[10px] text-amber-700">
                            {Math.round((supplierSuggestion.matched_code_count / (supplierSuggestion.total_codes || 1)) * 100)}% of codes match — tap to use or search manually below
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={applySupplierSuggestion}
                          className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-md px-2 py-1 transition-colors"
                          data-testid="crm-supplier-suggestion-accept"
                        >
                          Use
                        </button>
                        <button
                          onClick={() => setSupplierSuggestionDismissed(true)}
                          className="text-xs text-amber-700 hover:text-amber-900 px-1 py-1 rounded"
                          data-testid="crm-supplier-suggestion-dismiss"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}

                  {supplierSearchRefined && !supplierSearchExhausted && supplierSuggestion?.name && !supplierId && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="crm-supplier-refined-hint">
                      No exact match for <span className="font-semibold">{supplierSuggestion.name}</span> — refined to <span className="font-mono">{supplierQuery}</span>.
                    </p>
                  )}
                  {supplierSearchExhausted && !supplierId && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="crm-supplier-exhausted-hint">
                      No matches for the suggested supplier — search supplier as normal above.
                    </p>
                  )}
                  {/* Results panel — visually distinct from the search input
                      above so the rep clearly sees the input vs the list of
                      hits. Once a supplier is selected, the panel collapses
                      and a green confirmation card replaces it (below). */}
                  {!supplierId && (suppliersLoading || suppliers.length > 0 || (supplierQuery && !suppliersLoading)) && (
                    <div className="mt-2 rounded-xl overflow-hidden border-2 border-blue-200 bg-blue-50/30 shadow-sm" data-testid="crm-supplier-results-panel">
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-blue-800 bg-blue-100/70 border-b border-blue-200 flex items-center justify-between">
                        <span>Search results</span>
                        {!suppliersLoading && suppliers.length > 0 && <span className="font-semibold normal-case tracking-normal">Tap one to select</span>}
                      </div>
                      <div className="max-h-40 overflow-y-auto divide-y divide-blue-100 bg-white">
                        {suppliersLoading ? (
                          <div className="p-2 text-xs text-gray-500">Searching Zoho…</div>
                        ) : supplierError ? (
                          <div className="p-2 text-xs text-amber-700 flex items-start gap-1.5" data-testid="crm-supplier-error">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>{supplierError}</span>
                          </div>
                        ) : suppliers.length === 0 ? (
                          <div className="p-2 text-xs text-gray-500">No matches</div>
                        ) : (
                          suppliers.slice(0, 20).map((s) => (
                            <button
                              key={s.id}
                              onClick={() => { handleSupplierPick(s); }}
                              className="w-full text-left text-sm px-3 py-2 hover:bg-blue-50 transition-colors"
                              data-testid={`crm-supplier-option-${s.id}`}
                            >
                              {s.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Confirmation card — supplier picked. Bold green so it's
                      unmistakable. User can clear it via the X to search again. */}
                  {supplierId && (
                    <div className="mt-2 rounded-xl border-2 border-emerald-500 bg-emerald-50 px-3 py-2 flex items-center justify-between" data-testid="crm-supplier-selected">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <div className="text-sm font-semibold text-emerald-900 truncate">{supplierQuery}</div>
                      </div>
                      <button
                        onClick={() => { setSupplierId(''); setSupplierQuery(''); }}
                        className="text-xs text-emerald-800 hover:text-emerald-900 underline shrink-0"
                        data-testid="crm-supplier-clear"
                      >
                        Change
                      </button>
                    </div>
                  )}

                  {/* Reminder when the user has typed a query but hasn't yet
                      committed to a supplier. Hidden once one is selected. */}
                  {!supplierId && supplierQuery.trim() && !suppliersLoading && suppliers.length > 0 && (
                    <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1" data-testid="crm-supplier-must-select-hint">
                      <AlertTriangle className="w-3 h-3" />
                      Pick a supplier from the list above before submitting — typing alone won&apos;t link the supplier.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-semibold">Contribution %</Label>
                  <Input type="number" step="0.01" value={contribution} onChange={(e) => setContribution(e.target.value)} placeholder="e.g. 15" className={`mt-1 ${INPUT_CLS}`} data-testid="crm-contribution-input" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Follow-up (working days)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={followUpDays}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFollowUpDays(v === '' ? '' : parseInt(v, 10));
                    }}
                    placeholder="e.g. 3"
                    className={`mt-1 ${INPUT_CLS}`}
                    data-testid="crm-followup-input"
                  />
                  {followUpDays === 0 && (
                    <p className="text-[11px] text-amber-600 mt-1 font-medium">No follow-up task will be created in CRM</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-semibold">Quote Stage</Label>
                  <select
                    value={quoteStage}
                    onChange={(e) => setQuoteStage(e.target.value)}
                    className={`mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400`}
                    data-testid="crm-quote-stage-select"
                  >
                    {/* Quote_Stage picklist — values must match Zoho's
                        existing picklist EXACTLY (case + spacing) or Zoho
                        treats them as new entries and appends a duplicate
                        at the bottom of the list. The previous default
                        "Negotiation/Tender" with a slash was creating that
                        ghost stage on every quote. */}
                    <option value="Negotiation Tender">Negotiation Tender</option>
                    <option value="Negotiation Order to Place">Negotiation Order to Place</option>
                    <option value="Awaiting Supplier Information">Awaiting Supplier Information</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Closed Won Order in Progress">Closed Won Order in Progress</option>
                    <option value="Closed Won Order Completed">Closed Won Order Completed</option>
                    <option value="Closed Lost">Closed Lost</option>
                  </select>
                </div>
                {(selectedContractorPackageId || selectedSubContractorPackageId) && (
                  <div className="col-span-2 border-t border-dashed pt-3 mt-1">
                    <Label className="text-sm font-semibold flex items-center gap-1">
                      Package Closing Date
                      <span className="text-[10px] font-normal text-gray-500">(applied to the linked {contractorRole === 'main' ? 'main' : 'sub'}-contractor package in Zoho)</span>
                    </Label>
                    <Input
                      type="date"
                      value={packageClosingDate}
                      onChange={(e) => setPackageClosingDate(e.target.value)}
                      className={`mt-1 ${INPUT_CLS}`}
                      data-testid="crm-package-closing-date"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Also auto-populated when the quote is created: Package Status = <b>Quoted</b>, Package Amount = <b>£{(parseFloat(grandTotal || effectiveSubtotal || 0)).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</b>{businessSpecialism ? <>, Encon Brand = <b>{businessSpecialism}</b></> : null}{supplierId ? <>, Specified Supplier = <b>(from quote)</b></> : null}.
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 border rounded-lg p-3 text-xs text-gray-600 space-y-1">
                <div><span className="font-semibold">Date Quote Raised:</span> {todayISO} (today)</div>
                <div><span className="font-semibold">Quantity:</span> 1 · <span className="font-semibold">List Price:</span> £{(parseFloat(grandTotal || effectiveSubtotal || 0)).toLocaleString('en-GB', { maximumFractionDigits: 2 })} · <span className="font-semibold">Ex-VAT:</span> Yes</div>
                <div><span className="font-semibold">Quote Stage:</span> {quoteStage}</div>
                <div><span className="font-semibold">Quote PDF:</span> {effectivePdf ? `${effectivePdf.name} will be attached` : 'A fresh quote PDF will be generated and attached to the Zoho Quote_Upload field.'}</div>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep(mode === 'project' ? 2 : 1)}>← Back</Button>
                <div className="flex gap-2 flex-wrap justify-end">
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  {/* Chain → save the quote, close this modal, and open the
                      Add-Call modal pre-filled with the same account/contact.
                      Only shown on the create-quote flow when the parent
                      wired an `onChainToCall` handler. */}
                  {onChainToCall && (
                    <Button
                      onClick={() => handleSubmit({ chainCall: true })}
                      disabled={submitting || !productId || !subject?.trim()}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="crm-submit-then-call-btn"
                      title="Create the quote and immediately open the Add Call form pre-filled with the same account"
                    >
                      {submitting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating…</>) : (<><Phone className="w-4 h-4 mr-2" />Create & Then Call</>)}
                    </Button>
                  )}
                  <Button onClick={() => handleSubmit()} disabled={submitting || !productId || !subject?.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crm-submit-btn">
                    {submitting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating in Zoho…</>) : (<><ExternalLink className="w-4 h-4 mr-2" />Create Zoho Quote</>)}
                  </Button>
                </div>
              </div>
              </>
              )}
            </>
          )}

          {result?.quote_id && !showAccessRequest && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
                <div className="flex items-center gap-2 text-emerald-700 font-bold mb-2">
                  <CheckCircle2 className="w-5 h-5" /> Quote created in Zoho CRM
                </div>
                <div className="text-xs text-gray-700 space-y-1">
                  <div><span className="font-semibold">Quote ID:</span> {result.quote_id}</div>
                  <div><span className="font-semibold">Raised:</span> {formatDate(result.quote_raised_date)}</div>
                  {result.follow_up_date
                    ? <div><span className="font-semibold">Follow-up:</span> {formatDate(result.follow_up_date)}</div>
                    : <div><span className="font-semibold">Follow-up:</span> <span className="text-amber-600">None (no task created)</span></div>
                  }
                  <div>
                    <span className="font-semibold">PDF attached:</span>{' '}
                    {result.attachment_id
                      ? 'Yes'
                      : (
                          <>
                            No (non-fatal)
                            {result.pdf_error && (
                              <span className="text-amber-700"> — {result.pdf_error}</span>
                            )}
                          </>
                        )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                {/* Generate PDF */}
                <Button
                  onClick={handleGeneratePdf}
                  disabled={pdfGenerating}
                  className="bg-gray-700 hover:bg-gray-800 text-white inline-flex items-center gap-2"
                  data-testid="crm-success-generate-pdf-btn"
                >
                  {pdfGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  {pdfGenerating ? 'Generating…' : 'Download PDF'}
                </Button>
                {/* Add Call — opens the existing Add-Call modal pre-populated
                    with this quote's account so the rep can log a call
                    against the same customer in one click. */}
                <Button
                  onClick={() => setShowAddCallModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white inline-flex items-center gap-2"
                  data-testid="crm-success-add-call-btn"
                  title="Log a call against the same account"
                >
                  <Phone className="w-4 h-4" /> Add Call
                </Button>
                {result.quote_url && (
                  <a href={result.quote_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid="crm-open-zoho-link">
                    <ExternalLink className="w-4 h-4" /> Open in Zoho
                  </a>
                )}
                <Button variant="outline" onClick={onClose}>Close</Button>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Embedded Add-Call modal — rendered via portal into document.body so it
          sits above ALL other stacking contexts (including this modal's own
          backdrop-filter, which would otherwise confine fixed children). */}
      {showAddCallModal && effectiveAccount && createPortal(
        <AddCallToCrmModal
          isOpen={showAddCallModal}
          onClose={() => setShowAddCallModal(false)}
          prefillAccount={{
            account: {
              id: effectiveAccount.id,
              name: effectiveAccount.name || effectiveAccount.account_name || '',
              zoho_account_number: effectiveAccount.zoho_account_number || '',
            },
            contact: selectedContact ? {
              id: selectedContact.id,
              name: selectedContact.full_name || selectedContact.name || '',
            } : null,
          }}
          onSaved={() => {
            setShowAddCallModal(false);
            // Close the parent CRM modal too so the user lands back on the
            // page they came from with the call already logged.
            if (modalUiV2) activityBar.discardActive();
            onClose?.();
          }}
        />,
        document.body
      )}
      {/* Customer Management modal — opens when clicking customer name on a recent quote card */}
      {customerSearchName && (
        <ContactNotesTasksModal
          isOpen={true}
          onClose={() => setCustomerSearchName(null)}
          initialSearch={customerSearchName}
        />
      )}
      {historyItem && (
        <QuoteVersionHistoryModal
          quoteId={historyItem.id}
          customerName={(historyItem.quote_data || historyItem.email_data || {}).customer_name || historyItem.user_label || ''}
          quoteNumber={(historyItem.quote_data || {}).quote_number || ''}
          onClose={() => setHistoryItem(null)}
        />
      )}
    </div>
  );
};

export default AddToCrmModal;
